import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import BackButton from '../../../components/BackButton';
import LanguageFlags from '../../../components/LanguageFlags';
import { getImageUrl } from '../../../utils/getImageUrl';

const formatPageName = (name = '') => String(name) === 'BelongsTo'
  ? 'Belongs to'
  : String(name).replace(/([a-z])([A-Z])/g, '$1 $2');

const PageThumbnail = ({ page, pageLanguage, direction, onClick }) => {
  if (!page) return <div className="w-20" />;

  const previewKey = `image_preview_${pageLanguage}`;
  const imageSource = page[previewKey]
    ? getImageUrl(page[previewKey])
    : page.image_path
      ? `/uploads/${page.image_path}`
      : null;
  const isPrevious = direction === 'previous';

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1 group w-full"
      title={isPrevious ? `← ${page.name}` : `${page.name} →`}
    >
      <span className="text-xs text-gray-400 flex items-center gap-0.5">
        {isPrevious ? <ArrowLeft size={11} /> : null}
        {isPrevious ? 'Prev' : 'Next'}
        {isPrevious ? null : <ArrowRight size={11} />}
      </span>
      {imageSource ? (
        <img
          src={imageSource}
          alt={page.name}
          className="w-20 object-contain border border-gray-200 rounded shadow-sm group-hover:border-gray-400 transition-colors"
        />
      ) : (
        <div className="w-20 h-24 bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
          <span className="text-xs text-gray-400">No image</span>
        </div>
      )}
      <span className="text-xs text-gray-500 text-center line-clamp-2 w-20">{page.name}</span>
    </button>
  );
};

export default function EditPageHeader({
  displayPageNumber,
  pageData,
  pageLanguage,
  previousPage,
  nextPage,
  onPageLanguageChange,
  rendering = false,
  onPreviousPage,
  onNextPage,
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <div className="absolute inset-x-0 top-0 h-1 bg-emerald-600" />
      <BackButton className="p-2 rounded hover:bg-gray-200" />

      <div className="flex items-start gap-2">
        <div className="w-24 flex-shrink-0 flex flex-col items-center pt-1">
          <PageThumbnail
            page={previousPage}
            pageLanguage={pageLanguage}
            direction="previous"
            onClick={onPreviousPage}
          />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <span className="mb-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Page {displayPageNumber}
          </span>
          <Link
            to={`/pages/${pageData.book_id}?plang=${pageLanguage}`}
            className="text-xl font-bold text-slate-950 hover:text-emerald-700"
          >
            {pageData.book_name}
          </Link>
          <div className="mt-1 text-sm font-medium text-slate-600">{formatPageName(pageData.name)}</div>
          <div className="mt-2 flex items-center gap-2">
            <LanguageFlags language={pageLanguage} className="flex items-center gap-1" />
            <select
              disabled={rendering}
              value={pageLanguage}
              onChange={(event) => onPageLanguageChange(event.target.value)}
              className="border border-gray-300 rounded py-0.5 px-1.5 text-xs bg-white text-gray-600"
              data-testid="page-language-select"
            >
              <option value="es_de">ES-DE</option>
              <option value="es_en">ES-EN</option>
              <option value="es_ru">ES-RU</option>
              <option value="es_it">ES-IT</option>
              <option value="es_tr">ES-TR</option>
              <option value="es_fr">ES-FR</option>
              <option value="es_pl">ES-PL</option>
              <option value="de_nl">DE-NL</option>
              <option value="de_ru">DE-RU</option>
              <option value="de_en">DE-EN</option>
              <option value="de_it">DE-IT</option>
              <option value="de_fr">DE-FR</option>
              <option value="de_tr">DE-TR</option>
              <option value="de_pl">DE-PL</option>
              <option value="en_nl">EN-NL</option>
              <option value="en_pl">EN-PL</option>
              <option value="en_it">EN-IT</option>
              <option value="en_fr">EN-FR</option>
              <option value="en_tr">EN-TR</option>
              <option value="en_ru">EN-RU</option>
            </select>
          </div>
        </div>

        <div className="w-24 flex-shrink-0 flex flex-col items-center pt-1">
          <PageThumbnail
            page={nextPage}
            pageLanguage={pageLanguage}
            direction="next"
            onClick={onNextPage}
          />
        </div>
      </div>
    </div>
  );
}
